import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Checkbox,
  Divider
} from '@mui/material';

import {
  Search,
  FilterList,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Clear,
  Store,
  Assignment,
  Verified,
  CalendarToday,
  ThumbUp,
  ThumbUpAlt,
  History,
  Inventory,
  Receipt
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  '& .MuiTable-root': {
    minWidth: 800,
    width: '100%',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

// Helper function to get current user details from localStorage
const getCurrentUserDetails = () => {
  try {
    // Try to get user profile from localStorage (set during login)
    const userProfileStr = localStorage.getItem('userProfile');
    if (userProfileStr) {
      const userProfile = JSON.parse(userProfileStr);
      return {
        staff_id: userProfile.staff_id || userProfile.id || 'Unknown',
        full_name: userProfile.full_name || 
                   `${userProfile.first_name || ''} ${userProfile.other_names || ''}`.trim() || 
                   userProfile.email || 
                   'Auditor',
        first_name: userProfile.first_name,
        other_names: userProfile.other_names,
        email: userProfile.email,
        department: userProfile.department,
        user_type: userProfile.user_type,
        hotel_code: userProfile.hotel_code,
        phone: userProfile.phone
      };
    }
    
    // Fallback: try to get from individual localStorage items
    const staffId = localStorage.getItem('staffId') || localStorage.getItem('staff_id');
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const userRole = localStorage.getItem('role');
    const userDepartment = localStorage.getItem('department');
    
    return {
      staff_id: staffId || 'Unknown',
      full_name: userName || userEmail || 'Auditor',
      email: userEmail,
      department: userDepartment,
      user_type: userRole
    };
  } catch (error) {
    console.error('Error getting user details:', error);
    return {
      staff_id: 'Unknown',
      full_name: 'Auditor',
      email: '',
      department: '',
      user_type: ''
    };
  }
};

const Acc_Approval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [auditorName, setAuditorName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Selection states for bulk approval
  const [selectedSupplies, setSelectedSupplies] = useState([]);
  
  // Supplies states
  const [pendingSupplies, setPendingSupplies] = useState([]);
  const [approvedSupplies, setApprovedSupplies] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalInventory, setTotalInventory] = useState(0);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fetch pending supplies with pagination
  const fetchPendingSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_pending_supplies.php?${params}`);
      if (response.data.success) {
        setPendingSupplies(response.data.data);
        setTotalPending(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching pending supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch pending supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch approved supplies with pagination
  const fetchApprovedSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_approved_supplies.php?${params}`);
      if (response.data.success) {
        setApprovedSupplies(response.data.data);
        setTotalApproved(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching approved supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch approved supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch inventory items
  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_inventory_items.php?${params}`);
      if (response.data.success) {
        setInventoryItems(response.data.data);
        setTotalInventory(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch inventory items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, page, rowsPerPage]);

  // Fetch requisitions
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_requisitions.php?${params}`);
      if (response.data.success) {
        setRequisitions(response.data.data);
        setTotalRequisitions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch requisitions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateRange, page, rowsPerPage]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_categories.php`);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Effect to fetch data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchPendingSupplies();
    } else if (activeTab === 1) {
      fetchApprovedSupplies();
    } else if (activeTab === 2) {
      fetchInventoryItems();
    } else if (activeTab === 3) {
      fetchRequisitions();
    }
    fetchCategories();
  }, [activeTab, fetchPendingSupplies, fetchApprovedSupplies, fetchInventoryItems, fetchRequisitions, fetchCategories]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory, dateRange]);

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedSupplies([]);
    setPage(0);
  }, [activeTab]);

  // Get auditor name from localStorage - FIXED
  useEffect(() => {
    const userDetails = getCurrentUserDetails();
    setCurrentUser(userDetails);
    setAuditorName(userDetails.full_name);
    
    console.log('Loaded user details:', userDetails);
  }, []);

  // Approve single supply - UPDATED with user tracking
  const handleApproveSupply = async () => {
    setLoading(true);
    try {
      // Get fresh user details for audit trail
      const userDetails = getCurrentUserDetails();
      const approvedByName = userDetails.full_name;
      const approvedByStaffId = userDetails.staff_id;
      
      const payload = {
        id: selectedSupply.id,
        approved_by: approvedByName,
        approved_by_staff_id: approvedByStaffId,
        approval_date: new Date().toISOString(),
        notes: `Approved by ${approvedByName} (ID: ${approvedByStaffId})`
      };

      console.log('Approving supply with user:', { approvedByName, approvedByStaffId });

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/hotel/inventory/approve_supply.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `"${selectedSupply.item_name}" approved successfully by ${approvedByName}`,
          severity: 'success'
        });
        handleCloseApproveDialog();
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error approving supply:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Approve multiple supplies - UPDATED with user tracking
  const handleBulkApprove = async () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      // Get fresh user details for audit trail
      const userDetails = getCurrentUserDetails();
      const approvedByName = userDetails.full_name;
      const approvedByStaffId = userDetails.staff_id;
      
      const payload = {
        ids: selectedSupplies,
        approved_by: approvedByName,
        approved_by_staff_id: approvedByStaffId,
        approval_date: new Date().toISOString(),
        notes: `Bulk approved by ${approvedByName} (ID: ${approvedByStaffId})`
      };

      console.log('Bulk approving supplies with user:', { approvedByName, approvedByStaffId, count: selectedSupplies.length });

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/hotel/inventory/approve_multiple_supplies.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        setSelectedSupplies([]);
        setBulkApproveDialogOpen(false);
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error bulk approving supplies:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel - UPDATED with user info
  const handleExportToExcel = () => {
    const userDetails = getCurrentUserDetails();
    let exportData = [];
    let fileName = '';
    
    if (activeTab === 0) {
      exportData = pendingSupplies.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Requested By': item.added_by_staffid,
        'Status': 'Pending',
        'Exported By': userDetails.full_name,
        'Export Date': new Date().toLocaleString()
      }));
      fileName = 'pending_supplies';
    } else if (activeTab === 1) {
      exportData = approvedSupplies.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Requested By': item.added_by_staffid,
        'Approved By': item.checked_by,
        'Approval Date': item.updated_at,
        'Exported By': userDetails.full_name,
        'Export Date': new Date().toLocaleString()
      }));
      fileName = 'approved_supplies';
    } else if (activeTab === 2) {
      exportData = inventoryItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Current Stock': item.quantity_available,
        'Total Received': item.quantity_received,
        'Total Issued': item.quantity_issued,
        'Low Stock Threshold': item.lowstock_threshold,
        'Storage Location': item.storage_location,
        'Date Received': item.date_received,
        'Exported By': userDetails.full_name,
        'Export Date': new Date().toLocaleString()
      }));
      fileName = 'inventory_items';
    } else if (activeTab === 3) {
      exportData = requisitions.map(item => ({
        'Requisition ID': item.id,
        'Department': item.department,
        'Item Name': item.item_name,
        'Requested Quantity': item.quantity_requested,
        'Unit': item.unit_of_measurement,
        'Request Date': item.request_date,
        'Requested By': item.requested_by,
        'Status': item.status,
        'Notes': item.notes || 'N/A',
        'Exported By': userDetails.full_name,
        'Export Date': new Date().toLocaleString()
      }));
      fileName = 'requisitions';
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: `Export successful by ${userDetails.full_name}!`,
      severity: 'success'
    });
  };

  const handleOpenApproveDialog = (supply) => {
    setSelectedSupply(supply);
    setApproveDialogOpen(true);
  };

  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setSelectedSupply(null);
  };

  const handleOpenBulkApproveDialog = () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }
    setBulkApproveDialogOpen(true);
  };

  const handleCloseBulkApproveDialog = () => {
    setBulkApproveDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    setSelectedSupplies([]);
    setOrderBy('');
    setOrder('asc');
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    handleFilterClose();
  };

  // Handle select all/none
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = pendingSupplies.map((supply) => supply.id);
      setSelectedSupplies(newSelected);
    } else {
      setSelectedSupplies([]);
    }
  };

  // Handle single select
  const handleSelect = (id) => {
    const selectedIndex = selectedSupplies.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedSupplies, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedSupplies.slice(1));
    } else if (selectedIndex === selectedSupplies.length - 1) {
      newSelected = newSelected.concat(selectedSupplies.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedSupplies.slice(0, selectedIndex),
        selectedSupplies.slice(selectedIndex + 1),
      );
    }

    setSelectedSupplies(newSelected);
  };

  // Sort data
  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_received' || orderBy === 'total_invoice_value' || orderBy === 'quantity_available' || orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'date_received' || orderBy === 'request_date' || orderBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getCurrentData = () => {
    if (activeTab === 0) return sortData(pendingSupplies);
    if (activeTab === 1) return sortData(approvedSupplies);
    if (activeTab === 2) return sortData(inventoryItems);
    if (activeTab === 3) return sortData(requisitions);
    return [];
  };

  const filteredData = getCurrentData();
  const getTotalItems = () => {
    if (activeTab === 0) return totalPending;
    if (activeTab === 1) return totalApproved;
    if (activeTab === 2) return totalInventory;
    if (activeTab === 3) return totalRequisitions;
    return 0;
  };
  const totalItems = getTotalItems();

  // Statistics
  const statistics = useMemo(() => {
    const pendingCount = totalPending;
    const approvedCount = totalApproved;
    const inventoryCount = totalInventory;
    const requisitionCount = totalRequisitions;
    const selectedCount = selectedSupplies.length;
    
    return { pendingCount, approvedCount, inventoryCount, requisitionCount, selectedCount };
  }, [totalPending, totalApproved, totalInventory, totalRequisitions, selectedSupplies]);

  // Render Pending Supplies Table
  const renderPendingTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: 50 }}>
              <Checkbox
                indeterminate={selectedSupplies.length > 0 && selectedSupplies.length < pendingSupplies.length}
                checked={pendingSupplies.length > 0 && selectedSupplies.length === pendingSupplies.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No pending supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => (
              <TableRow key={item.id} hover selected={selectedSupplies.indexOf(item.id) !== -1}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedSupplies.indexOf(item.id) !== -1}
                    onChange={() => handleSelect(item.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                      <Pending sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{item.unit_of_measurement}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_received).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.date_received}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={`Requested by: ${item.added_by_staffid}`}>
                    <Chip 
                      label={item.added_by_staffid || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <GlowingButton
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<ThumbUp />}
                    onClick={() => handleOpenApproveDialog(item)}
                    sx={{ minWidth: 100 }}
                  >
                    Approve
                  </GlowingButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[3, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Approved Supplies Table
  const renderApprovedTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Added By</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Approved By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'updated_at'}
                direction={orderBy === 'updated_at' ? order : 'asc'}
                onClick={() => handleSort('updated_at')}
              >
                Approval Date
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No approved supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                      <Verified sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{item.unit_of_measurement}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_received).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.date_received}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.added_by_staffid || 'Unknown'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.checked_by || 'Auditor'} 
                    size="small" 
                    color="success"
                    variant="outlined"
                    icon={<Verified sx={{ fontSize: 14 }} />}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Inventory Table
  const renderInventoryTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_available'}
                direction={orderBy === 'quantity_available' ? order : 'asc'}
                onClick={() => handleSort('quantity_available')}
              >
                Current Stock
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Total Received
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_issued'}
                direction={orderBy === 'quantity_issued' ? order : 'asc'}
                onClick={() => handleSort('quantity_issued')}
              >
                Total Issued
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>Low Stock Alert</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Storage Location</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No inventory items found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => {
              const isLowStock = item.quantity_available <= item.lowstock_threshold && item.quantity_available > 0;
              const isOutOfStock = item.quantity_available <= 0;
              
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                        <Inventory sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category || 'Uncategorized'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      color={isOutOfStock ? 'error.main' : isLowStock ? 'warning.main' : 'text.primary'}
                    >
                      {Number(item.quantity_available).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_issued).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={item.lowstock_threshold || 5}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.storage_location || 'Not specified'} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Requisitions Table
  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 150 }}>Requisition ID</TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_requested'}
                direction={orderBy === 'quantity_requested' ? order : 'asc'}
                onClick={() => handleSort('quantity_requested')}
              >
                Requested Qty
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'request_date'}
                direction={orderBy === 'request_date' ? order : 'asc'}
                onClick={() => handleSort('request_date')}
              >
                Request Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Department</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No requisitions found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                      <Receipt sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_requested).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.request_date}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.requested_by} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.status}
                    size="small"
                    color={item.status === 'Received' ? 'success' : 
                           item.status === 'Issued' ? 'primary' :
                           item.status === 'Approved by GM' ? 'info' :
                           item.status === 'Approved by Dept Head' ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip label={item.department} size="small" variant="outlined" />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  const renderCurrentTable = () => {
    if (activeTab === 0) return renderPendingTable();
    if (activeTab === 1) return renderApprovedTable();
    if (activeTab === 2) return renderInventoryTable();
    if (activeTab === 3) return renderRequisitionsTable();
    return null;
  };

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflow: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      ml: isMobile ? 0 : sidebarOpen ? 0 : 0, 
        transition: 'margin 0.3s ease-in-out',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />
       
        <Box sx={{ 
          flexGrow: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          p: 3,
          maxWidth: '1600px',
          mx: 'auto',
          width: '100%'
        }}>
          <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="h5" fontWeight={700}>
                Account Approval Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Welcome, {auditorName}. Review and approve supply requests, view inventory and requisitions.
              </Typography>
              {currentUser?.department && (
                <Chip 
                  label={`Department: ${currentUser.department}`}
                  size="small"
                  sx={{ mt: 1, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
              )}
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                }
              }}
            >
              <Tab icon={<Pending />} iconPosition="start" label={`Pending (${statistics.pendingCount})`} />
              <Tab icon={<History />} iconPosition="start" label={`Approved (${statistics.approvedCount})`} />
              <Tab icon={<Inventory />} iconPosition="start" label={`Inventory (${statistics.inventoryCount})`} />
              <Tab icon={<Receipt />} iconPosition="start" label={`Requisitions (${statistics.requisitionCount})`} />
            </Tabs>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.01) }}>
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Pending Approvals
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                              {statistics.pendingCount}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <Pending />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Selected for Approval
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="primary.main">
                              {statistics.selectedCount}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <ThumbUpAlt />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </>
              )}
              {activeTab === 1 && (
                <Grid item xs={12}>
                  <StyledCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Total Approved Supplies
                          </Typography>
                          <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                            {statistics.approvedCount}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                          <Verified />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              )}
              {activeTab === 2 && (
                <Grid item xs={12}>
                  <StyledCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Total Inventory Items
                          </Typography>
                          <Typography variant="h4" fontWeight={700} mt={1} color="info.main">
                            {statistics.inventoryCount}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                          <Inventory />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              )}
              {activeTab === 3 && (
                <Grid item xs={12}>
                  <StyledCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Total Requisitions
                          </Typography>
                          <Typography variant="h4" fontWeight={700} mt={1} color="secondary.main">
                            {statistics.requisitionCount}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}>
                          <Receipt />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              )}
            </Grid>

            {/* Search and Filters Bar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name, category, or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <GlowingButton
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={handleFilterClick}
                    >
                      Filters
                    </GlowingButton>
                    
                    {activeTab === 0 && (
                      <GlowingButton
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUpAlt />}
                        onClick={handleOpenBulkApproveDialog}
                        disabled={selectedSupplies.length === 0}
                      >
                        Bulk Approve ({selectedSupplies.length})
                      </GlowingButton>
                    )}
                    
                    <GlowingButton
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExportToExcel}
                    >
                      Export
                    </GlowingButton>
                    
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setDateRange({ start: '', end: '' });
                      if (activeTab === 0) {
                        fetchPendingSupplies();
                      } else if (activeTab === 1) {
                        fetchApprovedSupplies();
                      } else if (activeTab === 2) {
                        fetchInventoryItems();
                      } else if (activeTab === 3) {
                        fetchRequisitions();
                      }
                    }}>
                      <Refresh />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Active Filters Display */}
            {(selectedCategory !== 'all' || dateRange.start || dateRange.end) && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                {selectedCategory !== 'all' && (
                  <Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />
                )}
                {dateRange.start && (
                  <Chip label={`From: ${dateRange.start}`} onDelete={() => setDateRange({ ...dateRange, start: '' })} size="small" />
                )}
                {dateRange.end && (
                  <Chip label={`To: ${dateRange.end}`} onDelete={() => setDateRange({ ...dateRange, end: '' })} size="small" />
                )}
                <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>Clear All</Button>
              </Box>
            )}

            {/* Table */}
            {renderCurrentTable()}
          </Paper>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, borderRadius: '16px' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 0 || activeTab === 1 || activeTab === 3) && (
          <>
            <TextField
              fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
              value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
              value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              sx={{ mb: 2 }}
            />
          </>
        )}
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      {/* Single Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog}>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>"{selectedSupply?.item_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quantity: {selectedSupply?.quantity_received} {selectedSupply?.unit_of_measurement}<br />
            Total Value: ₦{Number(selectedSupply?.total_invoice_value).toLocaleString()}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will be recorded as approved by <strong>{auditorName}</strong> and the supply will be added to inventory.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSupply}
            disabled={loading}
            startIcon={<ThumbUp />}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Confirmation Dialog */}
      <Dialog open={bulkApproveDialogOpen} onClose={handleCloseBulkApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>{selectedSupplies.length} supply(s)</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will be recorded as approved by <strong>{auditorName}</strong> and all selected supplies will be added to inventory.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Selected Items:</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
              {pendingSupplies
                .filter(s => selectedSupplies.includes(s.id))
                .map(s => (
                  <Typography key={s.id} variant="body2" sx={{ py: 0.5 }}>
                    • {s.item_name} ({s.quantity_received} {s.unit_of_measurement})
                  </Typography>
                ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApprove}
            disabled={loading}
            startIcon={<ThumbUpAlt />}
          >
            {loading ? <CircularProgress size={24} /> : `Approve All (${selectedSupplies.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Acc_Approval;